/**
 * Drawing the callout layer — the cloud, its scalloped outline, and the leader
 * that points at what the note is about.
 *
 * SCREEN SPACE, always. The caller enters it through `beginScreenSpaceOverlay`
 * (the one door — see its comment for the DPR and rotation traps a hand-rolled
 * mapping falls into) and hands us world → screen. Everything here is then in
 * CSS pixels, which is what makes a note the same readable size at every zoom.
 *
 * The overlay RETURNS what it drew. The hit test measures against that rather
 * than recomputing the layout, so a click is answered by the geometry the user
 * actually clicked on — the same contract the wiring overlay uses.
 */

import {
  CALLOUT_FONT,
  CALLOUT_LINE_HEIGHT_PX,
  CALLOUT_PADDING_PX,
  CALLOUT_SCALLOP_PX,
  calloutBoxSize,
  calloutRect,
  calloutsOf,
  leaderExitPoint,
  wrapCalloutText,
} from './calloutPlan';

/** Admin purple (`--color-admin`) — the feature is admin-gated throughout. */
const INK = '#7C3AED';
const PAPER = '#FFFFFF';
const SELECTED_PAPER = '#F5F0FF';
const TEXT = '#2E1065';
const PLACEHOLDER = '#9CA3AF';

/**
 * Half the angle each scallop subtends, measured from the outward normal.
 *
 * With the arc's centre pushed INSIDE the box by `r·cos(half-angle)`, the two
 * ends of every arc land exactly on the box edge, one step apart — so
 * neighbours meet at a cusp instead of crossing. That is what a revision
 * cloud is, and it is the reason the centre is inset rather than sitting on
 * the edge: centres on the edge need an arc of more than 180° to reach their
 * neighbours, and the surplus curls back over them as loops.
 *
 * At 75° the bulge is about 0.4 of the step — plump enough to read as a cloud
 * at a glance without swallowing the text's padding.
 */
const SCALLOP_SPREAD = (75 * Math.PI) / 180;

/**
 * Arcs are grown fractionally past touching so consecutive ends overlap.
 * Exactly touching leaves a hairline at every cusp once anti-aliasing has had
 * its say.
 */
const SCALLOP_OVERLAP = 1.06;

const ARROW_LEN = 9;
const ARROW_HALF_WIDTH = 3.6;

/**
 * Centre, outward direction and radius for every scallop around a box.
 *
 * Each side is divided into whole bumps so the pattern closes at the corners;
 * the radius follows from the step rather than being fixed, so a short side
 * gets smaller scallops instead of a lopsided last one.
 */
const scallopsAround = (rect, target = CALLOUT_SCALLOP_PX) => {
  const corners = [
    [rect.left, rect.top],
    [rect.right, rect.top],
    [rect.right, rect.bottom],
    [rect.left, rect.bottom],
  ];
  const out = [];
  for (let i = 0; i < 4; i += 1) {
    const [x0, y0] = corners[i];
    const [x1, y1] = corners[(i + 1) % 4];
    const len = Math.hypot(x1 - x0, y1 - y0);
    if (len <= 0) continue;
    const bumps = Math.max(2, Math.round(len / (target * 2)));
    const step = len / bumps;
    const dx = (x1 - x0) / len;
    const dy = (y1 - y0) / len;
    // The corners are wound top-left → top-right → bottom-right → bottom-left,
    // so (dy, -dx) is the outward normal of every side.
    const nAngle = Math.atan2(-dx, dy);
    // The radius that makes the arc's CHORD one step long, so its two ends
    // land on the step's own boundaries and meet the neighbouring arcs there.
    const r = (step / (2 * Math.sin(SCALLOP_SPREAD))) * SCALLOP_OVERLAP;
    // ...which only works with the centre pulled back inside the box; on the
    // edge the same arc would have to exceed a half-turn to reach that far.
    const inset = r * Math.cos(SCALLOP_SPREAD);
    for (let b = 0; b < bumps; b += 1) {
      const t = (b + 0.5) * step;
      out.push({
        x: x0 + dx * t - dy * inset,
        y: y0 + dy * t + dx * inset,
        nAngle,
        r,
      });
    }
  }
  return out;
};

/** The cloud's filled silhouette: the box plus every scallop, as one path. */
const traceCloud = (ctx, rect, scallops) => {
  ctx.beginPath();
  ctx.rect(rect.left, rect.top, rect.width, rect.height);
  // Same winding as `rect`, so the nonzero fill unions them rather than
  // punching the bumps back out of the box.
  for (const s of scallops) {
    ctx.moveTo(s.x, s.y);
    ctx.arc(
      s.x,
      s.y,
      s.r,
      s.nAngle - SCALLOP_SPREAD,
      s.nAngle + SCALLOP_SPREAD
    );
    ctx.closePath();
  }
};

/** Just the bumps, for the outline — the box's own edges are interior. */
const strokeScallops = (ctx, scallops) => {
  ctx.beginPath();
  for (const s of scallops) {
    const a0 = s.nAngle - SCALLOP_SPREAD;
    ctx.moveTo(s.x + Math.cos(a0) * s.r, s.y + Math.sin(a0) * s.r);
    ctx.arc(s.x, s.y, s.r, a0, s.nAngle + SCALLOP_SPREAD);
  }
  ctx.stroke();
};

/** Leader from the cloud's edge to the anchor, arrowhead on the anchor. */
const drawLeader = (ctx, from, to) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  // Inside its own cloud there is nothing to point at.
  if (len < ARROW_LEN) return;
  const ux = dx / len;
  const uy = dy / len;

  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x - ux * ARROW_LEN, to.y - uy * ARROW_LEN);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - ux * ARROW_LEN - uy * ARROW_HALF_WIDTH,
    to.y - uy * ARROW_LEN + ux * ARROW_HALF_WIDTH
  );
  ctx.lineTo(
    to.x - ux * ARROW_LEN + uy * ARROW_HALF_WIDTH,
    to.y - uy * ARROW_LEN - ux * ARROW_HALF_WIDTH
  );
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw one callout and report the box it landed in.
 *
 * @param {CanvasRenderingContext2D} ctx - In SCREEN space
 * @param {Object} callout
 * @param {(x: number, z: number) => {x: number, y: number}} worldToScreen
 * @param {Object} [options]
 * @param {boolean} [options.selected]
 * @param {string} [options.placeholder] - Shown when the note has no text yet
 * @returns {Object} The screen rect the cloud occupies
 */
export const drawCallout = (ctx, callout, worldToScreen, options = {}) => {
  const { selected = false, placeholder = 'Note' } = options;

  ctx.font = CALLOUT_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const measure = (s) => ctx.measureText(s).width;

  const body = String(callout?.text ?? '').trim();
  const lines = body ? wrapCalloutText(body, measure) : [placeholder];
  const rect = calloutRect(
    worldToScreen(callout.cloud.x, callout.cloud.z),
    calloutBoxSize(lines, measure)
  );
  const anchor = worldToScreen(callout.anchor.x, callout.anchor.z);
  const scallops = scallopsAround(rect);

  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // Leader first, so the cloud's fill covers the stub that would otherwise
  // show through the scalloped edge.
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.lineWidth = selected ? 2 : 1.4;
  drawLeader(ctx, leaderExitPoint(rect, anchor), anchor);

  traceCloud(ctx, rect, scallops);
  ctx.fillStyle = selected ? SELECTED_PAPER : PAPER;
  ctx.fill();

  ctx.strokeStyle = INK;
  ctx.lineWidth = selected ? 2 : 1.25;
  strokeScallops(ctx, scallops);

  ctx.fillStyle = body ? TEXT : PLACEHOLDER;
  const firstBaseline =
    rect.top + CALLOUT_PADDING_PX + CALLOUT_LINE_HEIGHT_PX / 2;
  lines.forEach((line, i) => {
    ctx.fillText(
      line,
      rect.left + CALLOUT_PADDING_PX,
      firstBaseline + i * CALLOUT_LINE_HEIGHT_PX
    );
  });

  ctx.restore();
  return rect;
};

/**
 * Draw every room's callouts, and report where each one landed.
 *
 * @param {CanvasRenderingContext2D} ctx - In SCREEN space
 * @param {Array<Object>} rooms
 * @param {(x: number, z: number) => {x: number, y: number}} worldToScreen
 * @param {Object} [options]
 * @param {string} [options.selectedId]
 * @param {Set<string>} [options.roomIds] - Only these rooms, when isolating
 * @returns {Array<{id: string, roomId: string, callout: Object, rect: Object}>}
 */
export const drawCalloutsOverlay = (
  ctx,
  rooms,
  worldToScreen,
  options = {}
) => {
  const { selectedId = null, roomIds = null } = options;
  const hits = [];
  for (const room of rooms || []) {
    if (roomIds && !roomIds.has(room?.id)) continue;
    for (const callout of calloutsOf(room)) {
      if (!callout?.anchor || !callout?.cloud) continue;
      const rect = drawCallout(ctx, callout, worldToScreen, {
        selected: callout.id === selectedId,
      });
      hits.push({ id: callout.id, roomId: room.id, callout, rect });
    }
  }
  return hits;
};

/**
 * The half-placed note the tool is still drawing: the leader from the anchor
 * to the cursor, so you can see where the cloud will hang before committing.
 *
 * @param {CanvasRenderingContext2D} ctx - In SCREEN space
 * @param {{x: number, z: number}} anchor
 * @param {{x: number, z: number}} cursor
 * @param {(x: number, z: number) => {x: number, y: number}} worldToScreen
 */
export const drawCalloutPreview = (ctx, anchor, cursor, worldToScreen) => {
  if (!anchor || !cursor) return;
  const a = worldToScreen(anchor.x, anchor.z);
  const c = worldToScreen(cursor.x, cursor.z);

  ctx.save();
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.lineWidth = 1.4;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.moveTo(c.x, c.y);
  ctx.lineTo(a.x, a.y);
  ctx.stroke();
  ctx.setLineDash([]);

  // Ring the anchor so it stays visible once the cursor has moved off it.
  ctx.beginPath();
  ctx.arc(a.x, a.y, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};
